import { memo, useCallback, useState, forwardRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { PipelineCardCompact } from './PipelineCardCompact';
import type { Customer, PipelineStage, PresenceUser } from '../../types';

export interface PipelineCardProps {
  customer: Customer;
  stage: PipelineStage;
  // Pre-computed state (avoids usePipelineContext which bypasses React.memo)
  isSelected: boolean;
  isHighlighted: boolean;
  isSearchMatch: boolean;
  searchActive: boolean;
  dimCard: boolean;
  isFiltering: boolean;
  highlightTerms: string[];
  isCopied: Record<string, boolean>;
  cardPresence: PresenceUser[];
  // Callbacks (stable refs from parent)
  copyToClipboard: (text: string | null | undefined, id: string) => void;
  onOpenDetail: (customer: Customer) => void;
  onDragStart: (customer: Customer) => void;
  onDragEnd: () => void;
  onUpdateLeadScore: (customerId: number, score: number) => void;
  // Ref registration
  registerRef: (customerId: number, node: HTMLDivElement | null) => void;
}

/**
 * Pipeline Card - Container component
 * Handles card state, drag-drop, and opens detail sheet on click
 *
 * NO LONGER calls usePipelineContext() or usePipelinePresence() -
 * everything is threaded as props so React.memo actually prevents re-renders
 */
const PipelineCardInner = forwardRef<HTMLDivElement, PipelineCardProps>(function PipelineCardInner({
  customer,
  stage,
  isSelected,
  isHighlighted,
  isSearchMatch,
  searchActive,
  dimCard,
  isFiltering,
  highlightTerms,
  isCopied,
  cardPresence,
  copyToClipboard,
  onOpenDetail,
  onDragStart,
  onDragEnd,
  onUpdateLeadScore,
  registerRef,
}, ref) {
  const prefersReducedMotion = useReducedMotion();
  const [isDragging, setIsDragging] = useState(false);

  // Compute emphasis class based on state
  let emphasisClass = '';
  if (isHighlighted) {
    emphasisClass = 'ring-2 ring-emerald-400 shadow-lg';
  } else if (isSelected) {
    emphasisClass = 'ring-2 ring-blue-400/60 shadow-lg';
  } else if (isSearchMatch) {
    emphasisClass = 'ring-1 ring-emerald-300/60 hover:ring-emerald-400/70';
  }

  // Register ref with context + forward ref from AnimatePresence
  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      registerRef(customer.id, node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [customer.id, registerRef, ref]
  );

  // Handle card click - opens detail sheet
  const handleClick = useCallback(() => {
    onOpenDetail(customer);
  }, [onOpenDetail, customer]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      setIsDragging(true);
      onDragStart(customer);

      // Create a ghost element for drag preview using safe DOM methods
      const ghostEl = document.createElement('div');
      ghostEl.classList.add('drag-ghost');

      const innerDiv = document.createElement('div');
      innerDiv.className = 'px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg text-sm font-medium';
      innerDiv.textContent = customer.name;
      ghostEl.appendChild(innerDiv);

      document.body.appendChild(ghostEl);
      e.dataTransfer.setDragImage(ghostEl, 0, 0);

      // Clean up ghost element after a delay
      setTimeout(() => {
        ghostEl.parentNode?.removeChild(ghostEl);
      }, 0);
    },
    [onDragStart, customer]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    onDragEnd();
  }, [onDragEnd]);

  // Opacity-only fade. No layout/layoutId, no spring, no scale — these are
  // the framer-motion features that hang the pipeline under load.
  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: isDragging ? 0.6 : 1 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
      style={{
        scrollMarginTop: '8px',
        scrollMarginBottom: '8px',
      }}
    >
      <Card
        ref={refCallback}
        className={`pipeline-card-modern overflow-hidden relative ${emphasisClass} ${dimCard ? 'opacity-40' : ''} ${isDragging ? 'card-dragging' : ''}`}
        onClick={handleClick}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        data-customer-id={customer.id}
        role="listitem"
      >
        <PipelineCardCompact
          customer={customer}
          cardPresence={cardPresence}
          highlightTerms={highlightTerms}
          isCopied={isCopied}
          copyToClipboard={copyToClipboard}
          onUpdateLeadScore={onUpdateLeadScore}
        />
      </Card>
    </motion.div>
  );
});

/**
 * Memoized PipelineCard - only re-renders when relevant props actually change
 * Now that useContext is removed, React.memo can effectively prevent re-renders
 */
export const PipelineCard = memo(PipelineCardInner, (prevProps, nextProps) => {
  return (
    prevProps.customer.id === nextProps.customer.id &&
    prevProps.customer.pipelineStageId === nextProps.customer.pipelineStageId &&
    prevProps.customer.name === nextProps.customer.name &&
    prevProps.customer.leadScore === nextProps.customer.leadScore &&
    prevProps.customer.pipelineNotes === nextProps.customer.pipelineNotes &&
    prevProps.customer.lastContactDate === nextProps.customer.lastContactDate &&
    prevProps.customer.nextContactDate === nextProps.customer.nextContactDate &&
    prevProps.customer.dealValue === nextProps.customer.dealValue &&
    prevProps.customer.email === nextProps.customer.email &&
    prevProps.customer.phone === nextProps.customer.phone &&
    prevProps.customer.address === nextProps.customer.address &&
    prevProps.customer.postcode === nextProps.customer.postcode &&
    prevProps.customer.source === nextProps.customer.source &&
    prevProps.customer.status === nextProps.customer.status &&
    prevProps.customer.additionalData === nextProps.customer.additionalData &&
    prevProps.customer.createdAt === nextProps.customer.createdAt &&
    prevProps.customer.updatedAt === nextProps.customer.updatedAt &&
    prevProps.stage.id === nextProps.stage.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.isSearchMatch === nextProps.isSearchMatch &&
    prevProps.searchActive === nextProps.searchActive &&
    prevProps.dimCard === nextProps.dimCard &&
    prevProps.isFiltering === nextProps.isFiltering &&
    prevProps.highlightTerms === nextProps.highlightTerms &&
    prevProps.isCopied === nextProps.isCopied &&
    prevProps.cardPresence === nextProps.cardPresence &&
    // Callbacks use stable refs (useCallback in parent), reference equality is fine
    prevProps.copyToClipboard === nextProps.copyToClipboard &&
    prevProps.onOpenDetail === nextProps.onOpenDetail &&
    prevProps.onDragStart === nextProps.onDragStart &&
    prevProps.onDragEnd === nextProps.onDragEnd &&
    prevProps.onUpdateLeadScore === nextProps.onUpdateLeadScore &&
    prevProps.registerRef === nextProps.registerRef
  );
});
